/**
 * As notas internas escritas sobre o CLIENTE (059), fora de qualquer negócio.
 *
 * ── A diferença para a nota da oportunidade ──────────────────────────────
 *
 * A da oportunidade guarda o que foi combinado NUMA negociação. Esta guarda o
 * que vale para além de qualquer uma: "prefere ser chamado depois das 18h",
 * "é sócio da oficina da rua de baixo". Pedida em 25/09/2026 para o painel do
 * cliente no atendimento — é ali que quem pega a conversa de outra pessoa
 * precisa saber com quem está falando.
 *
 * ── Quem lê e quem escreve ───────────────────────────────────────────────
 *
 * Quem vê o cliente anota. A regra da nota da oportunidade (editar o negócio)
 * deixaria de fora o agente, que só lê cadastro — e é ele quem mais atende.
 *
 * ── Editar e excluir (060) ────────────────────────────────────────────────
 *
 * A nota nasceu só acrescentando e, no mesmo dia, veio o pedido de editar e
 * excluir: é anotação de trabalho, e "prefere ser chamado à tarde" deixa de
 * valer. Altera quem escreveu, ou quem edita cadastro de cliente (o gestor
 * que arruma a nota de quem saiu de férias). A nota editada fica marcada.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

export const NotaDoClienteSchema = z.object({
  id: IdSchema,
  texto: z.string(),
  /** Nulo quando quem escreveu saiu da revenda: a nota fica, sem o nome. */
  autor: z.object({ id: IdSchema, nome: z.string().nullable() }).nullable(),
  criada_em: InstanteSchema,
  /** Nulo enquanto a nota for a original. Preenchido, a tela marca "editada". */
  editada_em: InstanteSchema.nullable(),
  /**
   * Quem pergunta pode editar e excluir esta nota?
   *
   * Vem do servidor, e não calculado na tela: a regra (autor, ou quem edita
   * cadastro de cliente) mora num lugar só. A tela só esconde o botão; quem
   * recusa é a rota.
   */
  pode_alterar: z.boolean(),
});

export type NotaDoCliente = z.infer<typeof NotaDoClienteSchema>;

/** Da mais recente para a mais antiga. */
export const NotasDoClienteSchema = z.object({
  itens: z.array(NotaDoClienteSchema),
});

export type NotasDoCliente = z.infer<typeof NotasDoClienteSchema>;

/**
 * O que se manda para escrever uma nota: só o texto.
 *
 * Sem título nem categoria — classificar obrigaria quem escreve a decidir antes
 * de escrever, e a nota precisa ser fácil de deixar no meio de um atendimento.
 */
export const CriarNotaDoClienteSchema = z
  .object({ texto: z.string().trim().min(1).max(2000) })
  .strict();

export type CriarNotaDoCliente = z.infer<typeof CriarNotaDoClienteSchema>;

/** Corrigir o texto: só o texto, como na criação. */
export const EditarNotaDoClienteSchema = z
  .object({ texto: z.string().trim().min(1).max(2000) })
  .strict();

export type EditarNotaDoCliente = z.infer<typeof EditarNotaDoClienteSchema>;
